// взято из https://github.com/roderickhsiao/react-in-viewport
import React, { useEffect, useRef, useState } from 'react';
import { findDOMNode } from 'react-dom';

type Config = {
    disconnectOnLeave?: boolean;
};

type Props = {
    onEnterViewport?: () => void;
    onLeaveViewport?: () => void;
    [key: string]: any;
};

type Options = IntersectionObserverInit;

const defaultOptions: Options = {};
const defaultConfig: Config = { disconnectOnLeave: false };
const defaultProps: Props = {
    onEnterViewport: () => ({}),
    onLeaveViewport: () => ({}),
};

const useInViewport = (
    target: React.RefObject<HTMLElement>,
    options: Options = defaultOptions,
    config: Config = defaultConfig,
    props: Props = defaultProps
) => {
    const { onEnterViewport, onLeaveViewport } = props;
    const [, forceUpdate] = useState<boolean>();

    const observer = useRef<IntersectionObserver | null>();

    const inViewportRef = useRef<boolean>(false);
    const intersected = useRef<boolean>(false);

    const enterCountRef = useRef<number>(0);
    const leaveCountRef = useRef<number>(0);

    function startObserver({ observerRef }: { observerRef: IntersectionObserver }) {
        const targetRef = target.current;
        if (targetRef) {
            const node = findDOMNode(targetRef);
            if (node) {
                observerRef?.observe(node as Element);
            }
        }
    }

    function stopObserver({ observerRef }: { observerRef: IntersectionObserver }) {
        const targetRef = target.current;
        if (targetRef) {
            const node = findDOMNode(targetRef);
            if (node) {
                observerRef?.unobserve(node as Element);
            }
        }

        observerRef?.disconnect();
        observer.current = null;
    }

    function handleIntersection(entries: IntersectionObserverEntry[]) {
        const entry = entries[0] || {};
        const { isIntersecting, intersectionRatio } = entry;
        const isInViewport = typeof isIntersecting !== 'undefined' ? isIntersecting : intersectionRatio > 0;

        // enter
        if (!intersected.current && isInViewport) {
            intersected.current = true;
            onEnterViewport?.();
            enterCountRef.current += 1;
            inViewportRef.current = isInViewport;
            forceUpdate(isInViewport);
            return;
        }

        // leave
        if (intersected.current && !isInViewport) {
            intersected.current = false;
            onLeaveViewport?.();
            if (config.disconnectOnLeave && observer.current) {
                // disconnect obsever on leave
                observer.current.disconnect();
            }
            leaveCountRef.current += 1;
            inViewportRef.current = isInViewport;
            forceUpdate(isInViewport);
        }
    }

    function initIntersectionObserver({ observerRef }: { observerRef: IntersectionObserver }) {
        if (!observerRef) {
            observer.current = new IntersectionObserver(handleIntersection, options);
            return observer.current;
        }
        return observerRef;
    }

    useEffect(() => {
        let observerRef = observer.current;
        // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
        observerRef = initIntersectionObserver({ observerRef } as { observerRef: IntersectionObserver });

        startObserver({
            observerRef,
        });

        return () => {
            stopObserver({ observerRef } as { observerRef: IntersectionObserver });
        };
    }, [target.current, options, config, onEnterViewport, onLeaveViewport]);

    return {
        inViewport: inViewportRef.current,
        enterCount: enterCountRef.current,
        leaveCount: leaveCountRef.current,
    };
};

export default useInViewport;
